// A time typed into a timesheet's field, as minutes from the company day's midnight (timesheet rules §3):
// "4:30", "4:30p", "430p", "16:30", "4.30 pm", "4p", "noon" and "midnight" all work. With no a.m. or p.m.
// the nearer of the two to the part's current time wins, and on a day that runs past midnight a time
// after it is typed as it is ("1:05"): it reads as the next day's (1440 and on) when that is nearer. The
// server works out the instant; this only reads the words.

export type TypedTime = { ok: true; minute: number } | { ok: false; error: string };

export interface TypedTimeOptions {
  /** Whether the next day's early hours can be meant (a part that runs past midnight). Default true. */
  nextDay?: boolean;
}

const SUFFIX = /^(a|p)\.?(?:m\.?)?$/;
const HINT = 'Type it like 4:30 p.m. or 16:30.';

/**
 * The minute `text` means, near `near` (the part's current time, in minutes from midnight; 1440 and on
 * are the next day's). Refused in words: not a time, an hour or minute that doesn't exist.
 */
export function parseTypedTime(text: string, near: number, opts: TypedTimeOptions = {}): TypedTime {
  const nextDay = opts.nextDay ?? true;
  const raw = text.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!raw) return { ok: false, error: 'Type a time.' };
  const pick = (bases: number[]) =>
    nearest(
      bases.flatMap((b) => (nextDay ? [b, b + 1440] : [b])),
      near,
    );
  if (raw === 'noon' || raw === 'midday') return { ok: true, minute: pick([720]) };
  if (raw === 'midnight') return { ok: true, minute: nearest(nextDay ? [0, 1440, 2880] : [0, 1440], near) };

  const m = /^(\d{1,4})(?:[:.](\d{1,2}))?\s?([ap](?:\.?m?\.?)?)?$/.exec(raw);
  if (!m) return { ok: false, error: `That isn’t a time. ${HINT}` };
  const [, digits, mins, suffix] = m as unknown as [string, string, string | undefined, string | undefined];
  let hour: number;
  let minute: number;
  if (mins !== undefined) {
    if (digits.length > 2 || mins.length !== 2) return { ok: false, error: `That isn’t a time. ${HINT}` };
    hour = Number(digits);
    minute = Number(mins);
  } else if (digits.length <= 2) {
    hour = Number(digits);
    minute = 0;
  } else {
    // "430" is 4:30 and "1630" is 16:30
    hour = Number(digits.slice(0, -2));
    minute = Number(digits.slice(-2));
  }
  if (minute > 59) return { ok: false, error: `An hour has 60 minutes: ${minute} isn’t one. ${HINT}` };

  if (suffix !== undefined) {
    if (!SUFFIX.test(suffix)) return { ok: false, error: `That isn’t a time. ${HINT}` };
    if (hour < 1 || hour > 12)
      return {
        ok: false,
        error: `${hour} ${suffix.startsWith('a') ? 'a.m.' : 'p.m.'} isn’t a time. ${HINT}`,
      };
    const pm = suffix.startsWith('p');
    return { ok: true, minute: pick([(hour % 12) * 60 + minute + (pm ? 720 : 0)]) };
  }
  if (hour > 24 || (hour === 24 && minute > 0))
    return { ok: false, error: `There’s no ${hour}:${String(minute).padStart(2, '0')}. ${HINT}` };
  if (hour === 24) return { ok: true, minute: nearest(nextDay ? [0, 1440, 2880] : [0, 1440], near) };
  // 0:xx and 13:00 and on say which half of the day they are; 1 to 12 could be either
  if (hour === 0 || hour > 12) return { ok: true, minute: pick([hour * 60 + minute]) };
  const am = (hour % 12) * 60 + minute;
  return { ok: true, minute: pick([am, am + 720]) };
}

/** The candidate nearest to `near`; between two as near, the later one (a bare "12" at 6 a.m. is noon). */
function nearest(candidates: number[], near: number): number {
  let best = candidates[0]!;
  for (const c of candidates) {
    const d = Math.abs(c - near);
    const b = Math.abs(best - near);
    if (d < b || (d === b && c > best)) best = c;
  }
  return best;
}
